import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSupabaseAuth } from '../../contexts/SupabaseAuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLoader, FiShield } = FiIcons;

const ProtectedRoute = ({ children, requiredRole, sellerOnly = false }) => {
  const { user, profile, loading, isInitialized } = useSupabaseAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // CRITICAL: If auth is initialized and no user exists, redirect immediately
    if (isInitialized && !loading && !user) {
      navigate('/login', { 
        state: { from: location.pathname }, 
        replace: true 
      });
    }
  }, [user, loading, isInitialized, navigate, location]);

  // 1. Show loader while checking auth state
  if (!isInitialized || loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex flex-col items-center justify-center bg-white space-y-4">
        <FiLoader className="w-12 h-12 text-indigo-600 animate-spin" />
        <p className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.3em]">Verifying Access...</p>
      </div>
    );
  }

  // 2. If no user, return null (the useEffect will handle the redirect)
  // This prevents the "Access Denied" flash after logout
  if (!user) return null;

  // 3. Role-based access checks (Only if user is logged in)
  
  // Seller Check
  if (sellerOnly && !profile?.is_seller && profile?.role !== 'seller' && profile?.role !== 'admin') {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white p-10 rounded-[2.5rem] shadow-xl text-center border border-gray-100">
          <SafeIcon icon={FiShield} className="text-4xl text-amber-600 mx-auto mb-6" />
          <h2 className="text-2xl font-black text-gray-900 mb-2">Seller Hub Restricted</h2>
          <p className="text-gray-500 font-medium mb-8">You need to be a registered seller to access this page.</p>
          <button 
            onClick={() => navigate('/profile#become-seller')}
            className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest"
          >
            Become a Seller
          </button>
        </div>
      </div>
    );
  }

  // Admin Check
  if (requiredRole === 'admin' && profile?.role !== 'admin') {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white p-10 rounded-[2.5rem] shadow-xl text-center border border-gray-100">
          <SafeIcon icon={FiShield} className="text-4xl text-red-600 mx-auto mb-6" />
          <h2 className="text-2xl font-black text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-500 font-medium mb-8">Administrator authorization required for this portal.</p>
          <button 
            onClick={() => navigate('/')}
            className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;